"use client";

import { FC, useState } from "react";
import { useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { Field, Input, Stack, Flex, Box, Text, Link } from "@chakra-ui/react";
import Image from "next/image";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import Button from "../../../components/Button";
import CenteredPageLayout from "../../../components/PageLayout/CenteredPageLayout";
import LoginAlert from "./LoginAlert";

type Inputs = {
  email: string;
  password: string;
};

const LoginPage: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { control, handleSubmit } = useForm<Inputs>();

  const searchParams = useSearchParams();
  const errorCode = searchParams?.get("error") ?? null;
  const userId = searchParams?.get("userId") ?? null;
  const email = searchParams?.get("email") ?? null;
  const activated = searchParams?.get("activated") === "true";

  const showCredentialsLogin =
    !errorCode ||
    ["CredentialsSignin", "PatreonMembershipMissing"].includes(errorCode);

  const showPatreonLogin =
    !activated &&
    (!errorCode ||
      ["CredentialsSignin", "PatreonMembershipMissing"].includes(errorCode));

  const showAlert = activated || errorCode;

  const onSubmit: SubmitHandler<Inputs> = async (
    { email, password },
    event,
  ) => {
    event?.preventDefault();

    setIsLoading(true);

    signIn("credentials", {
      redirect: true,
      callbackUrl: "/dashboard",
      email,
      password,
    });
  };

  return (
    <CenteredPageLayout
      title="Login"
      direction="column"
      links={
        showCredentialsLogin || showPatreonLogin
          ? [
              { label: "Register", url: "/register" },
              { label: "Forgot password", url: "/forgot-password" },
            ]
          : []
      }
    >
      {showAlert && (
        <LoginAlert
          activated={activated}
          errorCode={errorCode}
          userId={userId}
          email={email}
        />
      )}

      {showPatreonLogin && (
        <Flex alignItems="center" gap={4} fontWeight={600}>
          <Box flexShrink={0} bg="#fe5900" p={2}>
            <Image
              src="/images/pateron.png"
              alt="Patreon"
              width={30}
              height={33}
              style={{
                transform: "scale(0.8)",
              }}
            />
          </Box>

          <div>
            <Text as="h2">Already a Patreon member?</Text>

            <Link
              as="button"
              variant="underline"
              color="rgb(49, 130, 206)"
              onClick={() => signIn("patreon", { callbackUrl: "/dashboard" })}
            >
              Log in with Patreon
            </Link>
          </div>
        </Flex>
      )}

      {showCredentialsLogin && (
        <>
          {showPatreonLogin && (
            <Text as="h2" fontWeight={600} mb={3}>
              Log in with your email address
            </Text>
          )}

          <form action="post" onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={4} mt={3}>
              <Controller
                control={control}
                name="email"
                defaultValue=""
                rules={{
                  required: "Field required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <Field.Root invalid={!!error}>
                    <Field.Label>Email</Field.Label>
                    <Input {...field} type="email" />
                    {error && (
                      <Field.ErrorText>{error.message}</Field.ErrorText>
                    )}
                  </Field.Root>
                )}
              />

              <Controller
                control={control}
                name="password"
                defaultValue=""
                rules={{
                  required: "Field required",
                }}
                render={({ field, fieldState: { error } }) => (
                  <Field.Root invalid={!!error}>
                    <Field.Label>Password</Field.Label>
                    <Input {...field} type="password" />
                    {error && (
                      <Field.ErrorText>{error.message}</Field.ErrorText>
                    )}
                  </Field.Root>
                )}
              />

              <div>
                <Button visual="primary" type="submit" loading={isLoading}>
                  Login
                </Button>
              </div>
            </Stack>
          </form>
        </>
      )}
    </CenteredPageLayout>
  );
};

export default LoginPage;
